import { UploaderOptions, UploadInput, UploadOutput, UploadFile } from 'ngx-uploader';
import * as uuid from 'uuid';

import {
  Self,
  Input,
  Output,
  OnInit,
  Optional,
  Component,
  EventEmitter,
  ChangeDetectionStrategy,
} from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';

import { t } from '@lib/translation';

@Component({
  selector: 'sn-file-upload',
  templateUrl: './file-upload.component.html',
  styleUrls: ['./file-upload.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FileUploadComponent implements ControlValueAccessor, OnInit {
  @Input()
  get value(): File[] {
    return this._value;
  }
  set value(value: File[]) {
    this.writeValue(value);
    this._controlValueAccessorChangeFn(this.value);
  }

  @Input()
  public disabled = false;

  @Input()
  public multiple: boolean;

  @Input()
  public fileUploaded: (files: File[]) => void;

  @Input()
  public allowPreview = false;

  @Input()
  public allowedContentTypes: string[];

  @Input()
  public maxFileSize: number;

  @Input()
  public accept: string;

  @Output()
  public readonly valueChange = new EventEmitter<File[]>();

  public inputId: string;
  public options: UploaderOptions;
  public readonly uploadInput = new EventEmitter<UploadInput>();

  public dragOver: boolean;

  public files: UploadFile[] = [];

  private _value: File[];

  constructor(
    @Optional() @Self() private readonly _ngControl: NgControl,
  ) {
    if (this._ngControl) {
      this._ngControl.valueAccessor = this;
    }
    this.inputId = `upload-input-${uuid.v4()}`;
  }

  public ngOnInit(): void {
    this.options = {
      concurrency: 1,
      allowedContentTypes: this.allowedContentTypes,
      maxFileSize: this.maxFileSize,
    };

    if (!this.multiple) {
      this.options.maxUploads = 1;
    }
  }

  public writeValue(value: any): void {
    this._value = value;

    if (Array.isArray(value) && value.length > 0) {
      this.files = value.map((file: File) => {
        const uploadFile: any = {
          id: uuid.v4(),
          name: file.name,
          size: file.size,
          type: file.type,
          nativeFile: file,
          progress: { percent: 100 },
          status: 'Done',
          fileIndex: 0,
          lastModifiedDate: file.lastModified,
          form: null,
        };
        return uploadFile;
      });
    } else {
      this.files = [];
    }
  }

  public registerOnChange(fn: (value: any) => void): void {
    this._controlValueAccessorChangeFn = fn;
  }

  public registerOnTouched(): void {}

  public setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  public trackByFileId(index: number, item: UploadFile): any {
    return item.id;
  }

  public onUploadOutput(output: UploadOutput): void {
    switch (output.type) {
      case 'addedToQueue':
        if (output.file) {
          const addedIndex = this.files.findIndex((file) => file.id === output.file.id);

          if (addedIndex !== -1) {
            this.files[addedIndex] = output.file;
          } else {
            this.files.push(output.file);
          }
        }
        break;
      case 'allAddedToQueue':
        const nativeFiles = this.files.map((file) => file.nativeFile);

        this.value = nativeFiles;
        break;
      case 'rejected':
        alert(t('map.fileUpload.invalidFile'));
        this.files.splice(
          this.files.findIndex((file) => file.id === output.file.id),
          1,
        );
        this.value = this.files.map((file) => file.nativeFile);
        break;
      case 'removed':
        this.files.splice(
          this.files.findIndex((file) => file.id === output.file.id),
          1,
        );
        this.value = this.files.map((file) => file.nativeFile);
        break;
      case 'dragOver':
        this.dragOver = true;
        break;
      case 'dragOut':
      case 'drop':
        this.dragOver = false;
        break;
    }
  }

  public resetInput(): void {
    if (this.value) {
      const doc: any = document.getElementById(this.inputId);
      doc.value = '';
      this._value = null;
      this.files = [];
      this.uploadInput.next({ type: 'removeAll' });
    }
  }

  public onFileRemove(id: string): void {
    // Clear files array
    this.files = [];

    // Reset form control value
    this.value = [];

    // Reset internal state of ngx-uploader
    this.uploadInput.emit({ type: 'removeAll' });

    // Emit value change
    this.valueChange.emit(this.value);

    // Reset file input DOM element to clear previous selection
    const fileInput = document.getElementById(this.inputId) as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  }

  private _controlValueAccessorChangeFn: (value: any) => void = () => {};
}
